// 2_components/share_modal.js
// Product-detail share: intercepts Shoptet's .js-share-buttons-trigger and
// opens a custom Figma modal (copy link + Facebook / WhatsApp / e-mail).

const ICON_PATH = "/user/documents/upload/assets/icons/";
const iconCheck = ICON_PATH + "nat-share-check.svg";
const iconClose = ICON_PATH + "nat-share-close.svg";
const iconCopy = ICON_PATH + "nat-share-copy.svg";
const iconFacebook = ICON_PATH + "nat-share-facebook.svg";
const iconMail = ICON_PATH + "nat-share-mail.svg";
const iconWhatsapp = ICON_PATH + "nat-share-whatsapp.svg";

const MODAL_ID = "nat-share-modal";
const COPIED_MS = 2500;

let copiedTimer;
let lastActiveElement;

export function init() {
	document.addEventListener(
		"click",
		function (event) {
			const trigger = event.target.closest(".link-icon.share.js-share-buttons-trigger");
			if (!trigger) return;
			event.preventDefault();
			event.stopImmediatePropagation();
			openShareModal();
		},
		true,
	);
}

function getShareUrl() {
	const canonical = document.querySelector('link[rel="canonical"]');
	if (canonical?.href) return canonical.href;
	return window.location.href.split("#")[0];
}

function getProductName() {
	const heading = document.querySelector(".p-detail-inner-header h1") || document.querySelector(".product-top h1");
	if (!heading) {
		return document.title.replace(/\s*[|\-].*$/, "").trim();
	}
	const clone = heading.cloneNode(true);
	clone.querySelectorAll(".product-name-addition").forEach((node) => node.remove());
	return clone.textContent.replace(/\s+/g, " ").trim();
}

function openShareModal() {
	const modal = getOrCreateModal();
	const url = getShareUrl();
	const name = getProductName();

	modal.querySelector(".nat-share-modal__name").textContent = name;
	modal.querySelector(".nat-share-modal__url").textContent = url;
	modal.querySelector(".nat-share-modal__social--facebook").href =
		"https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(url);
	modal.querySelector(".nat-share-modal__social--whatsapp").href = "https://wa.me/?text=" + encodeURIComponent(url);
	modal.querySelector(".nat-share-modal__social--mail").href =
		"mailto:?subject=" + encodeURIComponent(name) + "&body=" + encodeURIComponent(url);

	resetCopiedState(modal);
	lastActiveElement = document.activeElement;
	modal.hidden = false;
	document.body.classList.add("scroll-lock");
	modal.querySelector(".nat-share-modal__close").focus();
}

function closeShareModal() {
	const modal = document.getElementById(MODAL_ID);
	if (!modal || modal.hidden) return;
	modal.hidden = true;
	document.body.classList.remove("scroll-lock");
	resetCopiedState(modal);
	if (lastActiveElement && typeof lastActiveElement.focus === "function") {
		lastActiveElement.focus();
	}
}

function resetCopiedState(modal) {
	clearTimeout(copiedTimer);
	const copyButton = modal.querySelector(".nat-share-modal__copy");
	copyButton.classList.remove("is-copied");
	copyButton.querySelector("img").src = iconCopy;
	copyButton.querySelector(".nat-share-modal__copy-label").textContent = window.NAT.translations("shareCopyLink");
}

function copyShareUrl(modal) {
	const url = modal.querySelector(".nat-share-modal__url").textContent;
	const done = function () {
		const copyButton = modal.querySelector(".nat-share-modal__copy");
		copyButton.classList.add("is-copied");
		copyButton.querySelector("img").src = iconCheck;
		copyButton.querySelector(".nat-share-modal__copy-label").textContent = window.NAT.translations("shareLinkCopied");
		clearTimeout(copiedTimer);
		copiedTimer = setTimeout(function () {
			if (!modal.hidden) resetCopiedState(modal);
		}, COPIED_MS);
	};

	if (navigator.clipboard?.writeText) {
		navigator.clipboard.writeText(url).then(done).catch(function () {
			fallbackCopy(url);
			done();
		});
		return;
	}
	fallbackCopy(url);
	done();
}

function fallbackCopy(url) {
	const textarea = document.createElement("textarea");
	textarea.value = url;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.left = "-9999px";
	document.body.appendChild(textarea);
	textarea.select();
	document.execCommand("copy");
	textarea.remove();
}

function createIcon(src, width, height) {
	const img = document.createElement("img");
	img.src = src;
	img.alt = "";
	img.width = width;
	img.height = height;
	return img;
}

function createSocialLink(modifier, src, width, height, labelKey) {
	const link = document.createElement("a");
	link.className = "nat-share-modal__social nat-share-modal__social--" + modifier;
	link.target = "_blank";
	link.rel = "noopener noreferrer";
	link.setAttribute("aria-label", window.NAT.translations(labelKey));
	link.appendChild(createIcon(src, width, height));
	return link;
}

function getOrCreateModal() {
	let modal = document.getElementById(MODAL_ID);
	if (modal) return modal;

	modal = document.createElement("div");
	modal.id = MODAL_ID;
	modal.className = "nat-share-modal";
	modal.hidden = true;

	const dialog = document.createElement("div");
	dialog.className = "nat-share-modal__dialog";
	dialog.setAttribute("role", "dialog");
	dialog.setAttribute("aria-modal", "true");
	dialog.setAttribute("aria-labelledby", "nat-share-modal-title");

	const closeButton = document.createElement("button");
	closeButton.type = "button";
	closeButton.className = "nat-share-modal__close";
	closeButton.setAttribute("aria-label", window.NAT.translations("shareClose"));
	closeButton.appendChild(createIcon(iconClose, 58, 56));

	const title = document.createElement("p");
	title.className = "nat-share-modal__title";
	title.id = "nat-share-modal-title";
	const prefix = document.createElement("span");
	prefix.className = "nat-share-modal__prefix";
	prefix.textContent = window.NAT.translations("sharePrefix");
	const name = document.createElement("strong");
	name.className = "nat-share-modal__name";
	title.appendChild(prefix);
	title.appendChild(name);

	const url = document.createElement("div");
	url.className = "nat-share-modal__url";

	const copyButton = document.createElement("button");
	copyButton.type = "button";
	copyButton.className = "nat-share-modal__copy";
	copyButton.appendChild(createIcon(iconCopy, 24, 24));
	const copyLabel = document.createElement("span");
	copyLabel.className = "nat-share-modal__copy-label";
	copyLabel.textContent = window.NAT.translations("shareCopyLink");
	copyButton.appendChild(copyLabel);

	const socials = document.createElement("div");
	socials.className = "nat-share-modal__socials";
	socials.appendChild(createSocialLink("facebook", iconFacebook, 16, 28, "shareFacebook"));
	socials.appendChild(createSocialLink("whatsapp", iconWhatsapp, 32, 32, "shareWhatsapp"));
	socials.appendChild(createSocialLink("mail", iconMail, 25, 20, "shareEmail"));

	dialog.appendChild(closeButton);
	dialog.appendChild(title);
	dialog.appendChild(url);
	dialog.appendChild(copyButton);
	dialog.appendChild(socials);
	modal.appendChild(dialog);

	modal.addEventListener("click", function (event) {
		if (event.target === modal) closeShareModal();
	});
	closeButton.addEventListener("click", closeShareModal);
	copyButton.addEventListener("click", function () {
		copyShareUrl(modal);
	});
	document.addEventListener("keydown", function (event) {
		if (event.key === "Escape" && !modal.hidden) closeShareModal();
	});

	document.body.appendChild(modal);
	return modal;
}
